import axios, { type AxiosInstance } from 'axios';

/**
 * Single axios instance pointed at billing_connector's REST endpoints
 * (`/api/*`). Authentication is now uniform — the portal trades its
 * username + password for a billing_connector JWT at
 * /api/auth/back-office/login and attaches the resulting token on every
 * subsequent call.
 */
export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Reads the session token from local storage every request — keeps things
 * working across tabs / sign-outs without subscribing the interceptor to a
 * store.
 *
 * Zustand's `persist` middleware wraps the saved state as
 *   `{ state: { session: {...} }, version: 0 }`
 * so we drill down through both levels. We also fall back to the flat
 * `parsed.auth?.token` shape in case any older sessions are still cached
 * after a deploy.
 */
function readToken(): string | null {
  try {
    const raw = localStorage.getItem('kiwasco_session');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      state?: { session?: { auth?: { token?: string } } };
      session?: { auth?: { token?: string } };
      auth?: { token?: string };
    };
    return (
      parsed.state?.session?.auth?.token ??
      parsed.session?.auth?.token ??
      parsed.auth?.token ??
      null
    );
  } catch {
    return null;
  }
}

api.interceptors.request.use((config) => {
  const token = readToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const url: string = error?.config?.url ?? '';
    const status: number | undefined = error?.response?.status;
    // Failed login / exchange calls handle their own errors — don't
    // global-redirect on those.
    const isAuthCall =
      url.includes('/auth/back-office/login') ||
      url.includes('/auth/back-office/exchange');
    if (status === 401 && !isAuthCall) {
      localStorage.removeItem('kiwasco_session');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);
